// Client transport for the server-side AI gateway (POST /api/ai/chat).
// Attaches the current Firebase ID token, retries ONCE with a force-refreshed token
// on 401, aborts at 55s, and throws AIError with user-safe messages keyed by the
// gateway's error code. This is the single client entry point — no vendor URLs, no
// bundled AI secrets, and no Memory Highway paths ever live here.
import { auth } from '../firebase';

export class AIError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'AIError';
    this.code = code;
  }
}

const USER_MESSAGES = {
  rate_limited: 'The mentor needs a short break — try again in a moment',
  ai_disabled: 'The mentor is offline right now — AI features are temporarily disabled',
  network: 'Connection problem — check your network and try again',
  upstream: "The mentor couldn't respond just now — please try again",
  unauthorized: 'Your session expired — please sign in again',
};

const toError = (code) =>
  new AIError(USER_MESSAGES[code] ? code : 'upstream', USER_MESSAGES[code] || USER_MESSAGES.upstream);

async function request(body) {
  const user = auth.currentUser;
  if (!user) throw toError('unauthorized');

  let token = await user.getIdToken();

  const doFetch = (tok) => {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 55000);
    return fetch('/api/ai/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
      body: JSON.stringify(body),
      signal: ac.signal,
    }).finally(() => clearTimeout(timer));
  };

  let resp;
  try {
    resp = await doFetch(token);
  } catch {
    throw toError('network');
  }

  if (resp.status === 401) {
    try {
      token = await user.getIdToken(true);
      resp = await doFetch(token);
    } catch {
      throw toError('unauthorized');
    }
  }

  if (!resp.ok) {
    let code = 'upstream';
    try {
      code = (await resp.json())?.error?.code || code;
    } catch {
      /* non-JSON error body — keep the default 'upstream' code */
    }
    throw toError(code);
  }

  return resp;
}

export async function aiChat({ task, messages, temperature, max_tokens, response_format }) {
  const resp = await request({ task, messages, temperature, max_tokens, response_format });
  return resp.json();
}

export async function aiChatStream({ task, messages, temperature, max_tokens }) {
  // Returns the raw Response so callers keep response.body.getReader() for SSE.
  return request({ task, messages, temperature, max_tokens, stream: true });
}
